import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { AssetManifestEntry } from "@amzn/afxtaco-types";
import { reducerType } from "../../redux/reducers";
import { getAssetManifest } from "../api/assets/assets";

interface UseAssetManifestOptions {
  projectId: string | undefined;
  /** Skip fetching (e.g. during the new-project flow before assets exist). */
  skip?: boolean;
}

interface UseAssetManifestResult {
  /** Whether the project has at least one generated asset in S3. */
  hasAssets: boolean;
  /** The full manifest entries (paths + timestamps). */
  assets: AssetManifestEntry[];
  /** Whether the manifest is currently being fetched. */
  isLoading: boolean;
}

/**
 * Fetches the project's asset manifest (all files in S3) and exposes a simple
 * `hasAssets` boolean. This is the source-of-truth for whether a project has
 * downloadable content, regardless of how the project was created (web app,
 * MCP, etc.).
 *
 * Re-fetches when `assetsStaleCounter` bumps (agent finished generating).
 */
export const useAssetManifest = ({
  projectId,
  skip = false,
}: UseAssetManifestOptions): UseAssetManifestResult => {
  const [assets, setAssets] = useState<AssetManifestEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const staleCounter = useSelector((state: reducerType) =>
    projectId ? (state.Projects.assetsStaleCounter[projectId] ?? 0) : 0,
  );

  const doFetch = useCallback(
    async (signal: AbortSignal) => {
      if (!projectId || skip) return;
      setIsLoading(true);
      try {
        const response = await getAssetManifest(projectId);
        if (signal.aborted) return;
        if (response?.success) {
          setAssets(response.data.assets);
        }
      } catch (error) {
        if (signal.aborted) return;
        console.error("Failed to fetch asset manifest:", error);
      } finally {
        if (!signal.aborted) setIsLoading(false);
      }
    },
    [projectId, skip],
  );

  // Fetch on mount / project change. Aborts in-flight requests on cleanup.
  useEffect(() => {
    if (!projectId || skip) return;
    const controller = new AbortController();
    doFetch(controller.signal);
    return () => controller.abort();
  }, [projectId, skip, doFetch]);

  // Re-fetch when assets become stale (agent finished generating).
  const prevStaleCounterRef = useRef(staleCounter);
  useEffect(() => {
    if (staleCounter <= prevStaleCounterRef.current || !projectId || skip) {
      prevStaleCounterRef.current = staleCounter;
      return;
    }
    prevStaleCounterRef.current = staleCounter;
    const controller = new AbortController();
    doFetch(controller.signal);
    return () => controller.abort();
  }, [staleCounter, projectId, skip, doFetch]);

  // Reset on project change.
  const prevProjectIdRef = useRef(projectId);
  useEffect(() => {
    if (prevProjectIdRef.current !== projectId) {
      setAssets([]);
      prevProjectIdRef.current = projectId;
    }
  }, [projectId]);

  return { hasAssets: assets.length > 0, assets, isLoading };
};
